import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { BlockUserButton } from '../../../../../graphdebate/src/components/Blocking/BlockUserButton';
import { useBlockedUsers } from '../../../../../graphdebate/src/hooks/useBlockedUsers';
import { Button } from '../../../components/Button/Button';
import { Tooltip } from '../../../components/GraphModal/Tooltip';
import { IconSendMessage } from '../../../components/icons/Icons';
import { useAppApolloClient } from '../../../hooks/useAppApolloClient';
import { useAuthProfile, usePermissions } from '../../../hooks/usePermissions';
import { ReactComponent as RemoveIcon } from '../../../icons/icon-remove.inline.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { openChatWindow } from '../../../redux/slices/chat-service';
import type { Maybe, Profile, Relation } from '../../../types/api';
import type { RequestFriendCallback } from '../../api/relation';
import { requestFriend, updateFriendStatus } from '../../api/relation';
import { AcceptRefuseInvitation } from './AcceptRefuseInvitation';
import { AddFriend } from './AddFriend';

const messages = defineMessages({
  startChatting: { defaultMessage: 'Send a message', id: 'XZGfTo' },
  pendingAuthorFriendRequest: { defaultMessage: 'Invitation sent, pending', id: 'NgHE/v' },
  pendingGuestFriendRequest: { defaultMessage: 'Pending invitation received', id: 'gU0BfQ' },
  removeFriend: { defaultMessage: 'Remove from my friends', id: 'V/ClF7' },
  removeFriendConfirm: {
    defaultMessage: 'Are you sure you want to remove {pseudo} from your friends',
    id: '8laBm3',
  },
});

type ProfileRelationActionsProps = {
  currentUserId: string;
  relationId: string;
  status: string;
  appendToBody?: boolean;
  placement?: string;
  profile: Profile | null;
  relation: Maybe<Relation>;
  hideRemoveFriendButton?: boolean;
  inviteCallback?: RequestFriendCallback;
  handleClose?: () => void;
  isMine?: boolean;
};
/**
 *
 * @param authedUserId
 * @param relationId
 * @param appendToBody
 * @param placement
 * @param props
 * @constructor
 */
export const ProfileRelationActions = ({
  relationId,
  currentUserId,
  appendToBody = false,
  placement,
  profile,
  relation,
  hideRemoveFriendButton = false,
  inviteCallback,
  handleClose,
  isMine = false,
  ...props
}: ProfileRelationActionsProps) => {
  const status = props.status;
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const client = useAppApolloClient();
  const { authUserId } = usePermissions();
  const {
    profile: { id: authProfileId = '' },
  } = useAuthProfile();
  const { isBlocked } = useBlockedUsers();
  const { pseudo, id: profileId = '', picture, user } = profile || {};
  const guestId = relation?.guest?.id;

  const startChatting = useCallback(() => {
    handleClose?.();
    openChatWindow(client, { userId: currentUserId, authUserId })(dispatch);
  }, [handleClose, client, currentUserId, authUserId, dispatch]);

  const removeFriendFn = useCallback(async () => {
    const confirmation =
      typeof window !== undefined &&
      // @TODO implement custom alert
      // eslint-disable-next-line no-alert
      window.confirm(formatMessage(messages.removeFriendConfirm, { pseudo }) as string);

    if (!confirmation) return;

    const resp = await updateFriendStatus({ relationId, profileId }, client, 'refused');
    inviteCallback?.(resp);
  }, [formatMessage, pseudo, relationId, profileId, client, inviteCallback]);

  const addFriendFn = useCallback(
    message =>
      requestFriend(
        {
          userId: currentUserId,
          profileId,
          message,
          authUserId,
          authProfileId,
        },
        client,
      ),
    [client, currentUserId, authUserId, authProfileId, profileId],
  );

  if (!profile || !authUserId || authUserId === currentUserId) return null;

  const isAuthedUserRelationGuest = authUserId === guestId;

  switch (status) {
    case 'approved':
      return (
        <div className="flex items-center gap-x-0.5">
          <Tooltip content={formatMessage(messages.startChatting)}>
            <Button variant="link" onClick={startChatting} className="no-underline px-0 flex-1 p-0">
              <IconSendMessage className="h-6 w-6 fill-gray-500 hover:fill-gray-dark" />
            </Button>
          </Tooltip>

          {!hideRemoveFriendButton && (
            <Tooltip content={formatMessage(messages.removeFriend)}>
              <Button variant="link" onClick={removeFriendFn} className="no-underline px-0 flex-1">
                <RemoveIcon
                  className="fill-gray-500 hover:fill-gray-dark"
                  style={{
                    width: '1em',
                    height: '1em',
                    transform: 'translateY(-3%)',
                  }}
                />
              </Button>
            </Tooltip>
          )}
        </div>
      );

    case 'pending':
      return (
        <span className="prose-caption text-gray-medium">
          {isAuthedUserRelationGuest ? (
            <AcceptRefuseInvitation
              isPending={true}
              relation={relation}
              authUserId={authUserId}
              callback={inviteCallback}
            />
          ) : (
            formatMessage(messages.pendingAuthorFriendRequest)
          )}
        </span>
      );

    default:
      const userIsBlocked = user?.id ? isBlocked(user.id) : false;
      if (userIsBlocked) return null;

      return (
        <div className="flex items-center">
          <AddFriend
            onAddFriend={addFriendFn}
            userId={currentUserId}
            appendToBody={appendToBody}
            placement={placement}
            inviteCallback={inviteCallback}
          />
          {!isMine && user?.id && (
            <BlockUserButton
              type="icon"
              userId={user.id}
              pseudo={pseudo}
              picture={picture}
              className="border-none hover:bg-transparent h-8 w-8"
              iconClassName="h-5 w-6"
              iconContainerClassName="top-[1px]"
            />
          )}
        </div>
      );
  }
};
